<template>
  <GenAILayout class="intent-selection">
    <template #nav>
      <cvt-top-nav-bar
        mode="LIGHT"
        :route="genAISelectTemplate"
        variant="embedded"
        :class="'cvt-top-nav-bar'"
        template-name="Let's get started!"
        :on-genai-wizard="true"
        :fixed="true"
        :light-logo="logo"
        :dark-logo="logo"
        :type="orgName"
        @back="navigateToChooseATemplate"
      />
    </template>

    <div class="intent-sidebar superfunnel-pane">
      <cvt-button
        :disabled="loading"
        class="back-btn"
        text=""
        icon="cvt-icon-arrow-left-05"
        @click="goBack"
      />

      <section>
        <div class="superfunnel-form">
          <label class="superfunnel-form__label">
            Enter your business name <span>*</span>
          </label>
          <cvt-input
            :value="businessName"
            :placeholder="'Enter your business name'"
            :required="true"
            class="superfunnel-form__input"
            @input="(e) => onBusinessNameInput(e.target.value)"
          />
          <p v-if="businessNameError" class="superfunnel-form__error">
            Business name is required.
          </p>

          <label class="superfunnel-form__label">
            Select your business industry <span>*</span>
          </label>
          <cvt-select
            class="superfunnel-form__input"
            mode="dropdown"
            :background-mode="'LIGHT'"
            :multiple="false"
            :value="businessIndustry"
            :placeholder="'Select your business industry'"
            @input="onBusinessIndustryChange"
          >
            <option
              v-for="category in industries"
              :key="category.value"
              :value="category.value"
            >
              {{ category.label }}
            </option>
          </cvt-select>
          <p v-if="businessIndustryError" class="superfunnel-form__error">
            Business industry is required.
          </p>

          <label class="superfunnel-form__label">
            Provide a brief description of your business <span>*</span>
          </label>
          <cvt-input
            type="textarea"
            rows="4"
            :value="businessDescription"
            :placeholder="'Provide a brief description of your business'"
            :required="true"
            class="superfunnel-form__input"
            @input="(e) => onBusinessDescriptionInput(e.target.value)"
          />
          <p v-if="businessDescriptionError" class="superfunnel-form__error">
            Business description is required.
          </p>
          <!-- User Theme Color -->
          <label class="superfunnel-form__label">
            Pick your theme color
          </label>
          <div class="theme-color-picker">
            <div
              class="theme-color-swatch"
              :style="{ backgroundColor: userThemeColor, width: '100px' }"
              @click="showColorPicker = !showColorPicker"
            >
              <span class="theme-color-hex">{{ userThemeColor }}</span>
            </div>
            <div v-if="showColorPicker" class="theme-color-picker-dropdown">
              <chrome-color-picker
                :value="userThemeColor"
                @input="onThemeColorChange"
              />
            </div>
          </div>

          <template v-if="isUserAdmin && templates.length > 0">
            <div>
              <label class="superfunnel-form__label">
                Select Preferred GenAI Template
              </label>
              <cvt-select
                id="template-select"
                class="superfunnel-form__input"
                :placeholder="'Select a template'"
                mode="dropdown"
                :background-mode="'LIGHT'"
                :multiple="false"
                :value="templateKey"
                @input="(value) => (templateKey = value)"
              >
                <option
                  v-for="opt in templates"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.name }}
                </option>
              </cvt-select>
            </div>
          </template>

          <cvt-button
            class="superfunnel-form__submit"
            text="Generate Landing Page"
            color="primary"
            shape="round"
            :disabled="isBuildDisabled"
            :loading="loading"
            @click="handleBuildFunnel"
          />
        </div>
      </section>
    </div>
    <div class="content-wrapper position-relative">
      <div ref="previewPane" class="preview-pane">
        <div
          :class="{
            content: true,
          }"
        >
          <div v-if="!siteId || !pageId" class="preview-placeholder">
            <span
              >Fill your business details to generate your landing page...</span
            >
          </div>
          <iframe
            v-else
            :src="previewUrl"
            class="preview-iframe"
            frameborder="0"
            width="100%"
            height="100%"
          ></iframe>

          <!-- Floating Use Template Button -->
          <cvt-button
            v-if="siteId && pageId"
            class="use-template-btn"
            text="Use this template"
            color="primary"
            shape="round"
            @click="useTemplate"
          />
        </div>
      </div>
    </div>
  </GenAILayout>
</template>

<script>
import { cvtRouteNodes } from '../../utils/routes'
import {
  GenAICategories,
  getSessionItemGenAIOnboarding,
  setSessionGenAiOnboarding,
} from './utils'
import { mapGetters, mapState } from 'vuex'
import { Notification } from 'element-ui'
import color from 'color'
import * as getters from '@/js/store/modules/getters'
import { V1Sites } from '../../store/services/api'
import { database } from '@/js/store/services/firebase.service'
import { Chrome } from 'vue-color'

import GenAILayout from './baseLayout.vue'

const sitesApi = new V1Sites()

export default {
  name: 'BuildLandingPage',
  components: {
    GenAILayout,
    'chrome-color-picker': Chrome,
  },
  data() {
    const { genAISelectTemplate } = cvtRouteNodes
    return {
      loading: false,
      genAISelectTemplate,
      templateLoading: true,
      businessName: getSessionItemGenAIOnboarding('businessName') || '',
      businessIndustry: getSessionItemGenAIOnboarding('industry') || '',
      businessDescription:
        getSessionItemGenAIOnboarding('businessDescription') || '',
      industries: GenAICategories,
      formTouched: false,
      siteId: null,
      pageId: null,
      loaderMessages: [
        'Gathering the perfect sections for your landing page...',
        'Designing layouts that match your brand palette...',
        'Adding the final polish so everything shines...',
      ],
      loaderIntervalId: null,
      loaderMessageIndex: 0,
      loaderResizeHandler: null,
      currentLoaderMessage: 'Generating your landing page...',
      templateKey: null,
      templates: [],
      userThemeColor: '#85369E',
      showColorPicker: false,
    }
  },
  computed: {
    ...mapGetters({
      theme: getters.AUTH_GET_USER_BUILDER_THEME,
      isUserAdmin: getters.AUTH_GET_USER_IS_ADMIN,
    }),
    ...mapState('globalTheme', {
      orgName: ({ globalTheme }) => globalTheme.OrgName,
    }),
    navColor() {
      if (this.theme.color) {
        const colorPrefix = [3, 6].includes(this.theme.color.length) ? '#' : ''
        return color(`${colorPrefix}${this.theme.color}`)
      }
      return ''
    },
    logo() {
      return this.navColor && this.navColor.dark()
        ? this.theme.logoAlt
        : this.theme.logo
    },
    isBuildDisabled() {
      return (
        !this.businessName.trim() ||
        !this.businessIndustry ||
        !this.businessDescription.trim() ||
        this.loading
      )
    },
    businessNameError() {
      return this.formTouched && !this.businessName.trim()
    },
    businessIndustryError() {
      return this.formTouched && !this.businessIndustry
    },
    businessDescriptionError() {
      return this.formTouched && !this.businessDescription.trim()
    },
    previewUrl() {
      if (!this.siteId || !this.pageId) return null
      const host = window.location.origin
      return `${host}/api/v1/sites/${this.siteId}/preview/${this.pageId}`
    },
  },
  async created() {
    await this.loadTemplateData()
  },
  beforeDestroy() {
    this.stopLoaderSequence()
  },
  methods: {
    async loadTemplateData() {
      this.templates = await sitesApi.getGenAITemplateCategories()
    },
    onBusinessNameInput(value) {
      this.businessName = value
      setSessionGenAiOnboarding('businessName', value)
    },
    onBusinessIndustryChange(value) {
      this.businessIndustry = value
      setSessionGenAiOnboarding('industry', value)
    },
    onBusinessDescriptionInput(value) {
      this.businessDescription = value
      setSessionGenAiOnboarding('businessDescription', value)
    },
    onThemeColorChange(color) {
      this.userThemeColor = color.hex
    },
    async handleBuildFunnel() {
      this.formTouched = true
      if (this.isBuildDisabled) return
      await this.buildTemplate()
    },
    goBack() {
      this.$router.push({
        name: cvtRouteNodes.chooseAtemplate,
      })
    },
    async buildTemplate() {
      if (this.loading) return
      if (
        !this.businessName ||
        !this.businessIndustry ||
        !this.businessDescription
      ) {
        return Notification({
          title: 'Missing info',
          message:
            'Please fill out all the details before building your funnel.',
          type: 'warning',
          position: 'bottom-right',
        })
      }

      this.loading = true
      this.startLoaderSequence()
      this.siteId = null
      this.pageId = null

      try {
        const response = await sitesApi.createFunnel({
          businessName: this.businessName,
          businessIndustry: this.businessIndustry,
          businessDescription: this.businessDescription,
          templateKey: this.templateKey,
          themeColor: this.userThemeColor,
        })

        this.siteId = response.siteId
        this.pageId = response.pageId

        Notification({
          title: 'Landing page generated!',
          message: 'You can now preview your landing page.',
          type: 'success',
          position: 'bottom-right',
        })
      } catch (error) {
        console.error('Error creating funnel:', error)
        Notification({
          title: 'Error',
          message: 'Failed to generate landing page. Please try again.',
          type: 'error',
          position: 'bottom-right',
        })
      } finally {
        this.loading = false
        this.stopLoaderSequence()
      }
    },
    navigateToChooseATemplate() {
      this.$router.push({
        name: cvtRouteNodes.chooseAtemplate,
      })
    },
    startLoaderSequence() {
      this.loaderMessageIndex = 0
      this.currentLoaderMessage = this.getCurrentLoaderMessage()
      this.updateLoaderOverlay()
      this.attachLoaderBoundsListener()

      if (this.loaderIntervalId) {
        clearInterval(this.loaderIntervalId)
      }
      const rotationLength = this.loaderMessages.length || 1
      this.loaderIntervalId = setInterval(() => {
        this.loaderMessageIndex = (this.loaderMessageIndex + 1) % rotationLength
        this.currentLoaderMessage = this.getCurrentLoaderMessage()
        this.updateLoaderOverlay()
      }, 2500)
    },
    stopLoaderSequence() {
      if (this.loaderIntervalId) {
        clearInterval(this.loaderIntervalId)
        this.loaderIntervalId = null
      }
      this.detachLoaderBoundsListener()
      this.$store.dispatch('loader/stop')
    },
    getCurrentLoaderMessage() {
      if (!this.loaderMessages.length) {
        return 'Building your funnel...'
      }
      return this.loaderMessages[this.loaderMessageIndex]
    },
    updateLoaderOverlay() {
      this.$store.dispatch('loader/start', {
        msg: this.currentLoaderMessage,
        route: 'pane',
        bounds: this.getLoaderBounds(),
      })
    },
    attachLoaderBoundsListener() {
      if (this.loaderResizeHandler) {
        window.removeEventListener('resize', this.loaderResizeHandler)
        window.removeEventListener('scroll', this.loaderResizeHandler, true)
      }
      this.loaderResizeHandler = () => {
        this.updateLoaderOverlay()
      }
      window.addEventListener('resize', this.loaderResizeHandler)
      window.addEventListener('scroll', this.loaderResizeHandler, true)
    },
    detachLoaderBoundsListener() {
      if (this.loaderResizeHandler) {
        window.removeEventListener('resize', this.loaderResizeHandler)
        window.removeEventListener('scroll', this.loaderResizeHandler, true)
        this.loaderResizeHandler = null
      }
    },
    getLoaderBounds() {
      const pane = this.$refs.previewPane
      if (!pane) {
        return null
      }
      const rect = pane.getBoundingClientRect()
      return {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      }
    },
    useTemplate() {
      if (this.siteId && this.pageId) {
        const host = window.location.origin
        const editorUrl = `${host}/editor/${this.siteId}/design/${this.pageId}`
        window.location.href = editorUrl
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import './styles/IntentSelection.style.scss';

.superfunnel-form {
  margin: 0 auto;
  margin-top: 50px;
}

.superfunnel-pane {
  border-right: 1px solid #e8eaef;
  padding: 45px !important;
}

.superfunnel-form__label {
  font-weight: 600;
  margin-bottom: 6px;
  display: block;
  color: #0f172a;

  span {
    color: #ff3b30;
  }
}

.superfunnel-form__input {
  margin-bottom: 12px;
}

.superfunnel-form__error {
  color: #d93025;
  font-size: 12px;
  margin: -6px 0 12px;
}

.superfunnel-form__submit {
  width: 100%;
  margin-top: 12px;
}

.theme-color-picker {
  position: relative;
  margin-bottom: 12px;
}

.theme-color-swatch {
  width: 100%;
  height: 48px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #e8eaef;
  transition: all 0.2s ease;

  &:hover {
    border-color: #0f172a;
    transform: scale(1.02);
  }
}

.theme-color-hex {
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  text-transform: uppercase;
}

.theme-color-picker-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 100;
  margin-top: 8px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 8px;
}

.preview-pane {
  width: 100%;
  height: 100%;
}

.preview-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fb;
  border-radius: 12px;
  color: #6b7280;
  font-weight: 600;
}

.preview-iframe {
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.use-template-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
}

:global(body.pane-loader-active .loader-wrapper) {
  position: fixed;
  top: var(--pane-loader-top, 0);
  left: var(--pane-loader-left, 0);
  width: var(--pane-loader-width, 100vw);
  height: var(--pane-loader-height, 100vh);
  background: rgba(255, 255, 255, 0.95);
  pointer-events: none;
}
</style>
